import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { ISubjectData } from 'modules/cabinet';
import { resetUser, setUser } from 'modules/global/store/user-slice';

import { $api, $apiWithoutClient, errorHandler, globalConst, useBack } from '../../global';
import { IProfileV2 } from '../components/register';
import { CheckSmsLogin, SendSmsLogin } from '../queries';
import { nextStep, setRegion, setStep, setSubject } from '../store';

export * from './use-register';
export * from './use-send-sms';
export * from './use-verify-sms';

export const useSendSms = () => {
  const t = useTranslations();

  return useMutation({
    mutationFn: SendSmsLogin,
    onSuccess: () => {
      message.success(t('tasdiqlash_kodi_jonatildi'));
    },
    onError: errorHandler()
  });
};

export const useCheckSms = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const onBack = useBack();
  const { push } = useRouter();
  const ticketId = useAppSelector(({ authModal }) => authModal.ticketId);
  const redirectUrl = useAppSelector(({ authModal }) => authModal.redirectUrl);

  return useMutation({
    mutationFn: CheckSmsLogin,
    onSuccess: async (data, { isSimpleLogin }) => {
      if (isSimpleLogin) {
        dispatch(setUser(data.user));
        return;
      }

      if (data.user.profile_type) {
        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: [globalConst.queryKeys.USER_DATA] }),
          queryClient.invalidateQueries({ queryKey: [globalConst.queryKeys.GET_HEART_DATA] })
        ]);
        if (redirectUrl) {
          push(redirectUrl);
        } else if (ticketId) {
          push(`${globalConst.pageNames.RESULTS}/${ticketId}`);
        } else {
          onBack();
        }
      } else {
        dispatch(setUser({ name: data.user.name, last_name: data.user.last_name, birthday: data.user.birthday }));
        dispatch(nextStep());
        dispatch(setSubject(data?.user?.subjects?.map((subject: ISubjectData) => subject.id)));
        dispatch(setRegion(data?.user?.address));
      }
    },
    onError: errorHandler()
  });
};

export const useLogOut = (onClose: () => void) => {
  const queryClient = useQueryClient();
  const { push } = useRouter();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: async () => {
      const { data } = await $api.post(`/logout`);

      return data;
    },
    onSuccess: async () => {
      await queryClient.removeQueries({ queryKey: [globalConst.queryKeys.USER_DATA] });
      push('/');
      onClose();
      dispatch(resetUser());
      dispatch(setStep(1));
    },
    onError: errorHandler()
  });
};

export const useCreateUser = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: IProfileV2) => {
      const res = await $apiWithoutClient.post(`/v2/profile/create`, values);

      return res.data;
    },
    onSuccess: async data => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [globalConst.queryKeys.USER_DATA] }),
        queryClient.invalidateQueries({ queryKey: [globalConst.queryKeys.GET_HEART_DATA] })
      ]);
      dispatch(setStep(8));
      dispatch(setUser(data.user));
    },
    onError: errorHandler()
  });
};
